import { z } from "zod";
import { uuid } from "./common";

/*
 * Ayurveda edition A5: pharmacy counter. Medicines are dispensed from a prescription (or over the counter) out of
 * the branch's stock, earliest expiry first, and one pharmacy invoice is issued in the same step, so stock and bill
 * always agree. Selling price (tax included), tax category and HSN come from the stock item.
 */

export const dispenseInput = z.object({
  patientId: uuid,
  branchId: uuid,
  prescriptionId: uuid.nullish().transform((v) => v ?? null),
  notes: z.string().trim().max(1000).nullish().transform((v) => v || null),
  lines: z
    .array(
      z.object({
        inventoryItemId: uuid,
        quantity: z.number().positive().max(9999).refine((v) => Math.abs(Math.round(v * 100) - v * 100) < 1e-6, "At most two decimals"),
        prescriptionItemId: uuid.nullish().transform((v) => v ?? null),
      }),
    )
    .min(1)
    .max(30),
});
export type DispenseInput = z.input<typeof dispenseInput>;

export const pharmacyQueueQuery = z.object({
  branchId: uuid.optional(),
  days: z.coerce.number().int().min(1).max(90).default(7),
  status: z.enum(["waiting", "all"]).default("waiting"),
});

/** A stock item that can be sold, with what is left in the branch (batches with stock, earliest expiry first). */
export interface SellableItem {
  id: string;
  name: string;
  unit: string;
  salePriceMinor: number | null;
  taxRatePercent: number | null;
  hsnCode: string | null;
  medicineId: string | null;
  onHand: number;
  /** Usable (not expired) stock, earliest expiry first. */
  batches: { batchId: string; batchNumber: string; expiryDate: string | null; onHand: number }[];
  trackBatches: boolean;
}

export interface PharmacyQueueRow {
  prescriptionId: string;
  issuedAt: string;
  patient: { id: string; fullName: string; patientCode: string; phone: string | null };
  doctorName: string | null;
  itemCount: number;
  /** Prescription lines with at least something dispensed. */
  dispensedCount: number;
  status: "waiting" | "partly" | "dispensed";
}

export interface PharmacyPrescription {
  id: string;
  issuedAt: string;
  patient: { id: string; fullName: string; patientCode: string };
  doctorName: string | null;
  items: {
    id: string;
    medicineId: string | null;
    medicineName: string;
    form: string | null;
    dosage: string;
    frequency: string;
    duration: string | null;
    durationDays: number | null;
    manufacturer: string | null;
    /** Already given from this line (in stock units). */
    dispensedQuantity: number;
    /** Stock items linked to this medicine (for the branch), best match first. */
    suggestions: SellableItem[];
  }[];
}

export interface Dispensing {
  id: string;
  patientId: string;
  branchId: string;
  prescriptionId: string | null;
  dispensedAt: string;
  dispensedBy: string | null;
  notes: string | null;
  invoice: { id: string; number: string | null; status: string; totalMinor: number; balanceMinor: number };
  items: { inventoryItemId: string; name: string; unit: string; batchNumber: string | null; expiryDate: string | null; quantity: number }[];
}
